import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuctionDetailPanel = ({ auction, onClose, onAuctionAction }) => {
  const [activeTab, setActiveTab] = useState('timeline');
  const [bidHistory, setBidHistory] = useState([]);

  useEffect(() => {
    if (auction) {
      // Mock bid history data
      setBidHistory([
        {
          id: 1,
          buyerId: 'buyer-001',
          buyerName: 'Premium Insurance Co.',
          amount: 45.50,
          timestamp: new Date(Date.now() - 30000),
          status: 'active'
        },
        {
          id: 2,
          buyerId: 'buyer-002',
          buyerName: 'SecureLife Partners',
          amount: 45.25,
          timestamp: new Date(Date.now() - 45000),
          status: 'outbid'
        },
        {
          id: 3,
          buyerId: 'buyer-003',
          buyerName: 'Guardian Insurance',
          amount: 45.00,
          timestamp: new Date(Date.now() - 60000),
          status: 'outbid'
        },
        {
          id: 4,
          buyerId: 'buyer-001',
          buyerName: 'Premium Insurance Co.',
          amount: 44.75,
          timestamp: new Date(Date.now() - 90000),
          status: 'outbid'
        },
        {
          id: 5,
          buyerId: 'buyer-004',
          buyerName: 'TrustShield Agency',
          amount: 44.50,
          timestamp: new Date(Date.now() - 120000),
          status: 'outbid'
        }
      ]);
    }
  }, [auction]);

  if (!auction) {
    return (
      <div className="w-96 bg-card border-l border-border h-full flex items-center justify-center">
        <div className="text-center">
          <Icon name="MousePointer" size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Select an auction to view details</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatTimestamp = (timestamp) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })?.format(timestamp);
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    
    if (seconds < 60) return `${seconds}s ago`;
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  const getBuyerQualificationStatus = (buyer) => {
    const statuses = ['qualified', 'pending', 'rejected'];
    return statuses?.[Math.floor(Math.random() * statuses?.length)];
  };

  const getQualificationColor = (status) => {
    switch (status) {
      case 'qualified': return 'text-success';
      case 'pending': return 'text-warning';
      case 'rejected': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const tabs = [
    { id: 'timeline', label: 'Timeline', icon: 'Clock' },
    { id: 'buyers', label: 'Buyers', icon: 'Users' },
    { id: 'metadata', label: 'Call Data', icon: 'Phone' },
    { id: 'routing', label: 'Routing', icon: 'Route' }
  ];

  return (
    <div className="w-96 bg-card border-l border-border h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">Auction Details</h3>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
          />
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Auction ID</span>
            <span className="text-xs font-mono text-foreground">{auction?.id}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Current Bid</span>
            <span className="text-sm font-semibold text-success">
              {formatCurrency(auction?.currentBid)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Time Remaining</span>
            <span className="text-xs font-mono text-foreground">
              {Math.floor((new Date(auction.endTime) - new Date()) / 60000)}:
              {Math.floor(((new Date(auction.endTime) - new Date()) % 60000) / 1000)?.toString()?.padStart(2, '0')}
            </span>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs?.map((tab) => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-xs font-medium transition-smooth ${
              activeTab === tab?.id
                ? 'text-primary border-b-2 border-primary bg-accent/5' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={14} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'timeline' && (
          <div className="p-4">
            <h4 className="text-sm font-medium text-foreground mb-3">Bid History</h4>
            <div className="space-y-3">
              {bidHistory?.map((bid, index) => (
                <div key={bid?.id} className="flex items-start space-x-3">
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    index === 0 ? 'bg-success' : 'bg-muted-foreground'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground truncate">
                        {bid?.buyerName}
                      </span>
                      <span className="text-sm font-semibold text-foreground">
                        {formatCurrency(bid?.amount)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-muted-foreground">
                        {formatTimestamp(bid?.timestamp)}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        bid?.status === 'active' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                      }`}>
                        {bid?.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'buyers' && (
          <div className="p-4">
            <h4 className="text-sm font-medium text-foreground mb-3">
              Participating Buyers ({auction?.participatingBuyers?.length})
            </h4>
            <div className="space-y-3">
              {auction?.participatingBuyers?.map((buyer) => {
                const qualificationStatus = getBuyerQualificationStatus(buyer);
                return (
                  <div key={buyer?.id} className="p-3 bg-muted/20 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-foreground">
                        {buyer?.name}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        qualificationStatus === 'qualified' 
                          ? 'bg-success/10 text-success'
                          : qualificationStatus === 'pending' ?'bg-warning/10 text-warning' :'bg-error/10 text-error'
                      }`}>
                        {qualificationStatus}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Last Bid:</span>
                        <span className="text-foreground ml-1">
                          {formatCurrency(buyer?.lastBid || 0)}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Response:</span>
                        <span className="text-foreground ml-1">
                          {buyer?.responseTime || 'N/A'}ms
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'metadata' && (
          <div className="p-4">
            <h4 className="text-sm font-medium text-foreground mb-3">Call Information</h4>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Type</label>
                  <p className="text-sm text-foreground">{auction?.callMetadata?.type}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">State</label>
                  <p className="text-sm text-foreground">{auction?.callMetadata?.state}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Age</label>
                  <p className="text-sm text-foreground">{auction?.callMetadata?.age}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Gender</label>
                  <p className="text-sm text-foreground">{auction?.callMetadata?.gender}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Zip Code</label>
                  <p className="text-sm text-foreground">{auction?.callMetadata?.zipCode}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Source</label>
                  <p className="text-sm text-foreground">{auction?.callMetadata?.trafficSource}</p>
                </div>
              </div>
              
              <div className="border-t border-border pt-3">
                <label className="text-xs text-muted-foreground">Additional Notes</label>
                <p className="text-sm text-foreground mt-1">
                  {auction?.callMetadata?.notes || 'No additional notes available'}
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'routing' && (
          <div className="p-4">
            <h4 className="text-sm font-medium text-foreground mb-3">Routing Decisions</h4>
            <div className="space-y-3">
              <div className="p-3 bg-muted/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">Current Winner</span>
                  <Icon name="Crown" size={16} className="text-warning" />
                </div>
                <p className="text-sm text-foreground">Premium Insurance Co.</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Bid: {formatCurrency(auction?.currentBid)} • Qualified: Yes
                </p>
              </div>
              
              <div className="space-y-2">
                <h5 className="text-xs font-medium text-foreground">Routing Rules Applied</h5>
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={14} className="text-success" />
                    <span className="text-xs text-foreground">State targeting match</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={14} className="text-success" />
                    <span className="text-xs text-foreground">Age demographic match</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={14} className="text-success" />
                    <span className="text-xs text-foreground">Operational hours valid</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertTriangle" size={14} className="text-warning" />
                    <span className="text-xs text-foreground">Traffic source preference</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Action Buttons */}
      <div className="p-4 border-t border-border">
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Pause"
            onClick={() => onAuctionAction('pause', auction?.id)}
          >
            Pause
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="Clock"
            onClick={() => onAuctionAction('extend', auction?.id)}
          >
            Extend
          </Button>
          <Button
            variant="destructive"
            size="sm"
            iconName="Square"
            onClick={() => onAuctionAction('force_close', auction?.id)}
          >
            Force Close
          </Button>
          <Button
            variant="secondary"
            size="sm"
            iconName="Route"
            onClick={() => onAuctionAction('manual_route', auction?.id)}
          >
            Manual Route
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuctionDetailPanel;